import { useState } from "react";
import axios from "axios";
import { Download, Check, Mail, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Lead-magnet form embedded at the end of blog articles.
 * Captures {name, email} and sends the chosen guide by email.
 *
 * Props:
 *   guide   – id of the guide (must match a key in backend GUIDES)
 *   eyebrow – small uppercase label
 *   title   – main headline
 *   subtitle – sentence under headline
 *   bullets – array of strings; what the user gets
 *   source  – tracking source (article slug)
 */
const LeadMagnet = ({ guide, eyebrow = "Mini-guía gratuita", title, subtitle, bullets = [], source }) => {
  const [form, setForm] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Por favor, escribe tu nombre y email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/lead`, {
        name: form.name.trim(),
        email: form.email.trim(),
        guide,
        source: source || null,
      });
      setSent(true);
      toast.success("¡Listo! Revisa tu email en unos minutos.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo enviar. Inténtalo otra vez.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <aside
      data-testid={`lead-magnet-${guide}`}
      className="my-16 relative bg-ivory border border-sand rounded-3xl overflow-hidden"
    >
      {/* Soft decorative blob */}
      <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-terracotta/15 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-sage/15 blur-3xl pointer-events-none" />

      <div className="relative grid md:grid-cols-5 gap-0">
        {/* Left: copy */}
        <div className="md:col-span-3 p-8 md:p-10 lg:p-12">
          <span className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-terracotta mb-5">
            <Download size={12} /> {eyebrow}
          </span>
          <h3 className="font-serif text-3xl md:text-4xl text-charcoal leading-[1.1]">
            {title}
          </h3>
          {subtitle && (
            <p className="mt-4 text-charcoal/70 text-[15.5px] leading-relaxed">
              {subtitle}
            </p>
          )}
          {bullets.length > 0 && (
            <ul className="mt-6 space-y-2.5">
              {bullets.map((b) => (
                <li key={b} className="flex items-start gap-2.5 text-[14.5px] text-charcoal/80">
                  <Check size={15} className="text-olive shrink-0 mt-1" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Right: form / success */}
        <div className="md:col-span-2 bg-white/60 backdrop-blur-sm border-t md:border-t-0 md:border-l border-sand p-8 md:p-10 flex flex-col justify-center">
          {sent ? (
            <div data-testid={`lead-magnet-${guide}-success`} className="text-center py-4">
              <div className="w-14 h-14 rounded-full bg-olive/15 text-olive flex items-center justify-center mx-auto">
                <Check size={26} />
              </div>
              <h4 className="mt-5 font-serif text-2xl text-charcoal">¡Listo!</h4>
              <p className="mt-3 text-charcoal/70 text-[14.5px] leading-relaxed">
                Te he enviado la mini-guía a <strong>{form.email}</strong>. Llega en menos de 5 minutos.
              </p>
              <p className="mt-3 text-charcoal/55 text-[12.5px] leading-relaxed">
                Si no la encuentras, revisa la carpeta de promociones o spam.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3.5">
              <p className="text-[11px] tracking-[0.25em] uppercase text-charcoal/55 mb-1">
                Recíbela ahora por email
              </p>
              <div>
                <label htmlFor={`lead-${guide}-name`} className="sr-only">Nombre</label>
                <input
                  id={`lead-${guide}-name`}
                  data-testid={`lead-magnet-${guide}-name`}
                  type="text"
                  required
                  placeholder="Tu nombre"
                  value={form.name}
                  onChange={update("name")}
                  className="w-full bg-white border border-sand rounded-xl px-4 py-3 text-[15px] text-charcoal placeholder:text-charcoal/40 focus:outline-none focus:border-terracotta transition-colors"
                />
              </div>
              <div className="relative">
                <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-charcoal/40" />
                <label htmlFor={`lead-${guide}-email`} className="sr-only">Email</label>
                <input
                  id={`lead-${guide}-email`}
                  data-testid={`lead-magnet-${guide}-email`}
                  type="email"
                  required
                  placeholder="tu@email.com"
                  value={form.email}
                  onChange={update("email")}
                  className="w-full bg-white border border-sand rounded-xl pl-10 pr-4 py-3 text-[15px] text-charcoal placeholder:text-charcoal/40 focus:outline-none focus:border-terracotta transition-colors"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid={`lead-magnet-${guide}-submit`}
                className="group w-full inline-flex items-center justify-center gap-2 bg-charcoal text-ivory rounded-full px-6 py-3.5 text-sm font-medium hover:bg-terracotta hover:text-charcoal transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? "Enviando..." : "Recibir mini-guía gratis"}
                {!loading && <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />}
              </button>
              <p className="text-[11.5px] text-charcoal/50 leading-relaxed">
                Sin spam. Sin venderte nada. Solo la guía y, de vez en cuando, algún recurso útil.
                Te puedes dar de baja con un clic.
              </p>
            </form>
          )}
        </div>
      </div>
    </aside>
  );
};

export default LeadMagnet;
